import React from 'react';
import ua from '@tencent/docs-user-agent';
import { BadgeService, Feature, ListItem } from '@tencent/workbench';
import i18n from '@i18next/i18n';
import BaseToolbarFeature from '@framework/workbench/common/feature/base-toolbar-feature';
import { SLIDE_ADAPTER } from '@framework/workbench/editor-adpater/slide-adapter';
import { getFontFamilyConfigData } from '@editor/src/js/constants/toolbar-data';
import { config } from './config';
import { initFontList } from '@src/utils/fonts';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { FontList } from '@src/utils/fonts/sync-font-list-loader';
import { IFontFamilyFeature } from './font-family-interface';
import appConfigInstance, { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import {
    ToolbarFileMenuConfig,
} from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';

@Feature({
    adapters: [SLIDE_ADAPTER],
})
export class FontFamilyFeature extends BaseToolbarFeature implements IFontFamilyFeature {
    id = config.id;
    config = config;

    public constructor(private badgeService: BadgeService) {
        super();
        // 是否使用云字体，统一采取CAC配置
        const showCloudFont = appConfigInstance.getConfig(ToolbarFileMenuConfig.cloudFont as any) ?? true;
        if (showCloudFont) {
            // 是否使用私有化字体
            const showPrivateFont = getCacConfigByKey('custom.toolbarFileMenu.privateFont', false);
            initFontList({
                customPrivateFont: showPrivateFont,
            });
            this.badgeService.showBadgeOn('toolbar', {
                key: ActionType.FONT,
                expire: new Date('2022-09-28 23:59:59').getTime(),
            });
        }
    }

    public getFontFamily(): any {
        const values = this.getMenuManager()?.toolbarMenu().values;
        return values?.font;
    }

    public getFontFamilyName(): string {
        const disableItems = this.getMenuManager()?.disableItemIds() || [];
        if (disableItems.find(item => item.id === this.id)) {
            return config.content;
        }

        const fontFamilyList = getFontFamilyConfigData();
        const font = this.getFontFamily();
        if (font === 'multi') return i18n.t('多种字体');
        if (font === '默认字体') return i18n.t('默认字体');
        // 避免数据异常时 fontFamily 显示为空，添加兜底值为 默认字体
        return fontFamilyList.find(item => item.id === font)?.content || font || i18n.t('默认字体');
    }

    public getFontList(): typeof FontList {
        return FontList;
    }

    public onFontChange(fontFamily: string, isDrawer: boolean): void {
        isDrawer ? this.drawerCallback(fontFamily) : this.callback(fontFamily);
    }

    /**
     * 字体下拉列表
     * @param isDrawer 是否是侧边栏的字体列表
     */
    public renderSelectList(isDrawer?: boolean) {
        const itemList: ListItem[] = getFontFamilyConfigData();
        const itemListWithStyle: ListItem[] = itemList.map(item => ({
            ...item,
            style: !this.shouldDisableFontFamilyStyle() ? {
                fontFamily: item.id,
            } : undefined,
        }));
        // 是否使用云字体，统一采取CAC配置
        const showCloudFont = appConfigInstance.getConfig(ToolbarFileMenuConfig.cloudFont as any) ?? true;
        const showPrivateFont = getCacConfigByKey('custom.toolbarFileMenu.privateFont', false);
        // 云字体列表
        const FontList = this.getFontList();
        return (
            // b 端构建问题，需要 ignore 这里
            // @ts-ignore
            <FontList
                disableCustomFont={!showCloudFont || showPrivateFont} // 私有化字体可以使用的时候，就不必展示C端的云字体列表了
                defaultFonts={itemListWithStyle}
                selectedFontFamily={this.getFontFamily() ?? ''}
                onFontChange={(fontFamily: string) => {
                    this.onFontChange(fontFamily, !!isDrawer);
                }}
            />
        );
    }

    private shouldDisableFontFamilyStyle(): boolean {
        // window qq 浏览器字体列表性能有问题，列表项太多且每一项都需要加载系统字体，在 qq 浏览器上会导致爆内存以致 crash
        return ua.isWindows && ua.isQQBrowser;
    }
}
